import { useState } from 'react';
import { OutboundProcess } from '@features/supplyChain/types';
import dayjs from 'dayjs';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/20/solid';
import { ClockIcon } from '@heroicons/react/24/outline';
import { getNoticeColors } from '@views/Noticeboard/utils';
import { getTotalPallets } from '@utils';
import { useSetAtom } from 'jotai';
import { SkuInfoLink } from '@tailwindComponents/SkuInfoModal';
import {
  despatchOrderedQuantity,
  despatchTotalProducts,
  hasProductLines,
} from '../../../utils/despatch';
import { selectedShipmentNoteNumberAtom } from '../../../store/jotai';
import { ShipmentStatusBadge } from './ShipmentStatusBadge';

interface CollapsibleOutboundRowProps {
  outboundProcess: OutboundProcess;
  selected: boolean;
  onSelectChange: (id: string, checked: boolean) => void;
  onOpenProcess: (outboundProcess: OutboundProcess) => void;
}

export function CollapsibleOutboundRow({
  outboundProcess,
  selected,
  onSelectChange,
  onOpenProcess,
}: CollapsibleOutboundRowProps) {
  const [open, setOpen] = useState(false);
  const setSelectedShipmentNoteNumber = useSetAtom(
    selectedShipmentNoteNumberAtom
  );

  const { notices, goodsReceivedNote } = outboundProcess;

  const hasIssues = (notices?.length ?? 0) > 0;
  const isProcessComplete = !!goodsReceivedNote?.finalisedAt;
  const hasIssuesBeforeProcessIsComplete = hasIssues && !isProcessComplete;
  const hasIssuesWhenProcessIsComplete = hasIssues && isProcessComplete;
  const hasNoIssuesWhenProcessIsComplete = !hasIssues && isProcessComplete;

  const noteNumber =
    outboundProcess.shipmentNote?.shipmentNoteNumber ??
    outboundProcess.despatchNote?.despatchNoteNumber ??
    '';

  const customerName =
    outboundProcess.shipmentNote?.customerName ??
    outboundProcess.despatchNote?.customer?.name ??
    outboundProcess.despatchNote?.customerName ??
    '';

  const { despatchNote } = outboundProcess;

  const skus = outboundProcess.shipmentNote
    ? outboundProcess.shipmentNote.skus
    : despatchNote?.skus ?? [];

  const products = outboundProcess.shipmentNote
    ? []
    : despatchNote?.products ?? [];
  const isCountedInProducts =
    !outboundProcess.shipmentNote && hasProductLines(despatchNote);

  const totalSkuQuantity = outboundProcess.shipmentNote
    ? skus.reduce((prev, current) => prev + current.skuQuantity, 0)
    : despatchOrderedQuantity(despatchNote);

  const totalProducts = outboundProcess.shipmentNote
    ? skus.reduce(
        (prev, current) =>
          prev + current.skuQuantity * (current.amountOfProductInSku || 1),
        0
      )
    : despatchTotalProducts(despatchNote);
  const showProducts = totalProducts !== totalSkuQuantity;
  const skuUnits = skus.map((sku) => sku.unitOfMeasure).filter(Boolean);
  const unitsWord =
    skuUnits.length > 0 &&
    skuUnits.every((u) => u === skuUnits[0]) &&
    (skuUnits[0] || '').toLowerCase() !== 'other'
      ? (skuUnits[0] || '').toLowerCase()
      : 'unit';

  const totalPallets = skus
    .map((sku) => {
      return getTotalPallets(
        sku.skuQuantity,
        sku.packagingDetails?.packagingInfo?.amountOfSkusInLayer ?? 0,
        sku.packagingDetails?.packagingInfo?.amountOfLayersInPallet ?? 0
      );
    })
    .reduce((prev, current) => prev + current, 0);

  return (
    <div className='border-t border-gray-200'>
      <div className='flex items-start gap-5 border-b border-gray-200 p-5'>
        {/* Expand/Collapse chevron */}
        <button
          type='button'
          aria-label='expand row'
          onClick={() => setOpen(!open)}
          className='shrink-0 text-gray-400 hover:text-gray-600'
        >
          {open ? (
            <ChevronDownIcon className='h-6 w-6' />
          ) : (
            <ChevronRightIcon className='h-6 w-6' />
          )}
        </button>

        {/* Status badge + Note ID */}
        <div className='flex flex-1 flex-col items-center justify-center gap-2.5 max-w-[160px] min-w-[120px] px-2.5'>
          <button
            type='button'
            aria-label='manage shipment'
            onClick={() => onOpenProcess(outboundProcess)}
            className='rounded-full focus:outline-none focus:ring-2 focus:ring-blue-200'
          >
            <ShipmentStatusBadge status={outboundProcess.status} />
          </button>
          {open && (
            <button
              type='button'
              onClick={() => setSelectedShipmentNoteNumber(noteNumber)}
              className='flex items-center text-sm text-gray-950 hover:text-blue-600'
            >
              <span>id</span>
              <span className='text-base leading-4 w-4 text-center'>#</span>
              <span>{noteNumber.slice(-7)}</span>
              {hasIssuesBeforeProcessIsComplete && (
                <ExclamationCircleIcon
                  className='ml-1 h-4 w-4'
                  style={{ color: getNoticeColors('warning').fill }}
                />
              )}
              {hasIssuesWhenProcessIsComplete && (
                <ExclamationCircleIcon
                  className='ml-1 h-4 w-4'
                  style={{ color: getNoticeColors('error').fill }}
                />
              )}
              {hasNoIssuesWhenProcessIsComplete && (
                <CheckCircleIcon className='ml-1 h-4 w-4 text-green-500' />
              )}
            </button>
          )}
        </div>

        {/* Note ID */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <button
            type='button'
            onClick={() => setSelectedShipmentNoteNumber(noteNumber)}
            className='text-sm font-normal leading-5 text-blue-600 hover:text-blue-800 truncate text-left'
          >
            {noteNumber}
          </button>
        </div>

        {/* From */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <p className='text-sm font-normal leading-5 text-gray-950 truncate'>
            {outboundProcess.shipmentNote?.customerName
              ? 'Warehouse'
              : customerName}
          </p>
          {open && (
            <div className='flex flex-col gap-0.5 pt-1.5'>
              <div className='flex items-center gap-1'>
                <ClockIcon className='h-4 w-4 shrink-0 text-gray-500' />
                <span className='text-sm font-semibold text-gray-950'>
                  Pick-Up
                </span>
              </div>
              <div className='flex flex-wrap items-center gap-0.5'>
                <span className='text-sm text-gray-950'>
                  {dayjs(outboundProcess.shipmentNote?.createdAt).format(
                    'DD/MM/YYYY'
                  )}
                </span>
                <span className='mx-0.5 inline-block h-1 w-1 rounded-full bg-gray-950' />
                <span className='text-sm text-gray-950'>
                  {dayjs(outboundProcess.shipmentNote?.createdAt).format(
                    'HH:mm'
                  )}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* To (customer) */}
        <div className='flex flex-1 flex-col gap-1.5 min-w-[100px]'>
          <p className='text-sm font-normal leading-5 text-gray-950 truncate'>
            {customerName}
          </p>
          {open && (
            <div className='flex flex-col gap-0.5 pt-1.5'>
              <div className='flex items-center gap-1'>
                <ClockIcon className='h-4 w-4 shrink-0 text-gray-500' />
                <span className='text-sm font-semibold text-gray-950'>ETA</span>
              </div>
              <div className='flex flex-wrap items-center gap-0.5'>
                <span className='text-sm text-gray-950'>
                  {outboundProcess.shipmentNote?.estimatedTimeOfArrival
                    ? dayjs(
                        outboundProcess.shipmentNote.estimatedTimeOfArrival
                          .dateTime
                      ).format('DD/MM/YYYY')
                    : '-'}
                </span>
                {outboundProcess.shipmentNote?.estimatedTimeOfArrival && (
                  <>
                    <span className='mx-0.5 inline-block h-1 w-1 rounded-full bg-gray-950' />
                    <span className='text-sm text-gray-950'>
                      {dayjs(
                        outboundProcess.shipmentNote.estimatedTimeOfArrival
                          .dateTime
                      ).format('HH:mm')}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SKUs */}
        <div className='flex flex-1 flex-col gap-1.5 max-w-[170px] min-w-[90px]'>
          {isCountedInProducts ? (
            <p className='text-sm font-medium text-gray-900'>
              {totalProducts.toLocaleString()}
              <span className='ml-1 text-xs font-normal text-gray-500'>
                products
              </span>
            </p>
          ) : showProducts ? (
            <div className='leading-tight'>
              <p className='text-sm font-medium text-gray-900'>
                {totalProducts.toLocaleString()}
                <span className='ml-1 text-xs font-normal text-gray-500'>
                  products
                </span>
              </p>
              <p className='text-xs text-gray-500'>
                {totalSkuQuantity.toLocaleString()} {unitsWord}
                {totalSkuQuantity === 1 ? '' : 's'}
              </p>
            </div>
          ) : (
            <p className='text-sm font-normal leading-5 text-gray-900'>
              {totalSkuQuantity.toLocaleString()}
            </p>
          )}
          {open && (
            <div className='flex flex-col gap-2 pt-1.5'>
              {skus.map((sku) => {
                const skuProducts =
                  sku.skuQuantity * (sku.amountOfProductInSku || 1);
                const unit = (sku.unitOfMeasure || 'unit').toLowerCase();
                return (
                  <div
                    key={sku.skuId}
                    className='flex min-h-[2.25rem] flex-col leading-tight'
                  >
                    <p className='text-sm text-gray-950 truncate'>
                      <SkuInfoLink inventorySkuId={sku.skuId}>
                        {sku.skuIdentifier}
                      </SkuInfoLink>
                    </p>
                    <p className='text-xs text-gray-500'>
                      {(sku.amountOfProductInSku || 1) > 1 &&
                        `${skuProducts.toLocaleString()} products · `}
                      {sku.skuQuantity.toLocaleString()} {unit}
                      {sku.skuQuantity === 1 ? '' : 's'}
                    </p>
                  </div>
                );
              })}
              {products.map((product) => (
                <div
                  key={product.productId}
                  className='flex min-h-[2.25rem] flex-col leading-tight'
                >
                  <p className='text-sm text-gray-950 truncate'>
                    {product.productName || product.productId}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {product.productQuantity.toLocaleString()} products
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pallets */}
        <div className='flex flex-1 flex-col gap-1.5 max-w-[150px] min-w-[74px]'>
          <p className='text-sm font-normal leading-5 text-gray-900'>
            {totalPallets}
          </p>
          {open && (
            <div className='flex flex-col gap-2 pt-1.5'>
              {skus.map((sku) => {
                const packagingInfo = sku.packagingDetails?.packagingInfo;
                const pallets = packagingInfo
                  ? getTotalPallets(
                      sku.skuQuantity,
                      packagingInfo.amountOfSkusInLayer,
                      packagingInfo.amountOfLayersInPallet
                    )
                  : 0;
                return (
                  <p
                    key={sku.skuIdentifier}
                    className='flex min-h-[2.25rem] items-start text-sm text-gray-900'
                  >
                    {packagingInfo
                      ? `${pallets} ${packagingInfo.type}${
                          pallets > 1 ? 's' : ''
                        }`
                      : '—'}
                  </p>
                );
              })}
              {products.map((product) => (
                <p
                  key={product.productId}
                  className='flex min-h-[2.25rem] items-start text-sm text-gray-900'
                >
                  —
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Checkbox */}
        <div className='shrink-0'>
          <input
            type='checkbox'
            checked={selected}
            onChange={(e) =>
              onSelectChange(outboundProcess.id, e.target.checked)
            }
            className='h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500'
          />
        </div>
      </div>
    </div>
  );
}
